import { useState } from 'react';
import { ShoppingCart, Menu, X, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { useLanguage } from '@/context/LanguageContext';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { cartItemCount, setIsCartOpen } = useCart();
  const { lang, toggleLang, t } = useLanguage();

  const navLinks = [
    { name: t('nav_inicio'), href: '#inicio' },
    { name: t('nav_menu'), href: '#menu' },
    { name: t('nav_galeria'), href: '#galeria' },
    { name: t('nav_resenas'), href: '#resenas' },
    { name: t('nav_contacto'), href: '#contacto' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header 
      className="fixed top-0 left-0 right-0 z-50 glass border-b border-[#2D231E]/10"
      data-testid="header"
    >
      <div className="container-max">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a 
            href="#inicio" 
            onClick={(e) => { e.preventDefault(); scrollToSection('#inicio'); }}
            className="flex items-center gap-2"
            data-testid="logo-link"
          >
            <span className="font-serif text-2xl md:text-3xl font-bold text-[#C44536]">
              Taj Mahal
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                className="text-[#5C4D43] hover:text-[#C44536] transition-colors font-medium text-sm"
                data-testid={`nav-${link.href.replace('#', '')}`}
              >
                {link.name}
              </a>
            ))}
          </nav>

          {/* Language Toggle, Cart & Mobile Menu */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Allergens link */}
            <a
              href="/alergenos"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 text-[#5C4D43] hover:text-[#C44536] transition-colors font-medium text-xs px-2"
              data-testid="header-allergens-link"
              title="Informacion de alergenos"
            >
              <span aria-hidden="true">⚠</span>
              <span>Alergenos</span>
            </a>

            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLang}
              className="flex items-center gap-1.5 text-[#5C4D43] hover:text-[#C44536] px-2 md:px-3"
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4" />
              <span className="font-sans text-xs font-semibold uppercase">{lang === 'es' ? 'EN' : 'ES'}</span>
            </Button>

            {/* Cart */}
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={() => setIsCartOpen(true)}
              data-testid="cart-button"
            >
              <ShoppingCart className="w-5 h-5 text-[#2D231E]" />
              {cartItemCount > 0 && (
                <span 
                  className="absolute -top-1 -right-1 w-5 h-5 bg-[#C44536] text-[#FDFBF7] text-xs rounded-full flex items-center justify-center font-medium"
                  data-testid="cart-count"
                >
                  {cartItemCount}
                </span>
              )}
            </Button>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-[#2D231E]" />
              ) : (
                <Menu className="w-6 h-6 text-[#2D231E]" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav 
            className="md:hidden py-4 border-t border-[#E8DEC8]"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                  className="px-4 py-3 text-[#5C4D43] hover:text-[#C44536] hover:bg-[#F5EFE6] rounded-lg transition-colors font-medium"
                >
                  {link.name}
                </a>
              ))}
              <a
                href="/alergenos"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-3 text-[#5C4D43] hover:text-[#C44536] hover:bg-[#F5EFE6] rounded-lg transition-colors font-medium flex items-center gap-2"
                data-testid="mobile-allergens-link"
              >
                <span aria-hidden="true">⚠</span> Alergenos
              </a>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
